/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateCreator } from 'zustand';
import { createAppStore } from '@marriott/mi-store-utils';
import { produce } from 'immer';
import { SelectedInteractionsPropsType } from '../../context/SessionUpdateContext/index.types';

// Interface for session update interaction tracking state
export interface SessionUpdateState {
  interactionsSelected: SelectedInteractionsPropsType;
  revisionToken: string | null;
  sessionObject: {
    consumerID: string;
    accessToken: string;
    sessionID: string;
    rewardsId?: string;
  } | null;
}

// Interface for store actions
export interface SessionUpdateActions {
  // Maintain compatibility with existing SessionUpdateContext API
  setInteractionsSelected: (values: SelectedInteractionsPropsType) => void;

  // Additional convenience methods for better state management
  setInteractionSelected: (key: keyof SelectedInteractionsPropsType, value: boolean) => void;
  resetInteractions: () => void;

  // Initialize store with existing interaction state for migration
  initializeInteractions: (interactions: Partial<SelectedInteractionsPropsType>) => void;

  // Revision token management
  setRevisionToken: (token: string | null) => void;
  
  // Session object management
  setSessionObject: (sessionObject: SessionUpdateState['sessionObject']) => void;
  updateSessionObject: (updates: Partial<NonNullable<SessionUpdateState['sessionObject']>>) => void;
}

// Combined store type
export type SessionUpdateStore = SessionUpdateState & SessionUpdateActions;

// Initial state matching the existing SessionUpdateContext default values
const initialState: SessionUpdateState = {
  interactionsSelected: {
    // Match the exact structure from SessionUpdateContext
    availability: false,
    price: false,
    points: false,
    amenities: false,
    brands: false,
    distance: false,
    transportation: false,
    hotelType: false,
    events: false,
    activities: false,
    states: false,
    city: false,
    pagination: false,
    taxesAndFees: false,
    currencyConversion: false,
    sortBy: false,
    quickFilters: false,
    savedProperties: false,
    listOrMapButton: false,
  },
  revisionToken: null,
  sessionObject: null,
};

// Store creator following existing patterns
const sessionUpdateZustandStore: StateCreator<SessionUpdateStore> = set => {
  return {
    ...initialState,

    // Maintain compatibility with existing SessionUpdateContext API
    // This method matches the exact signature used by components
    setInteractionsSelected: (values: SelectedInteractionsPropsType) => {
      set(
        produce(draft => {
          draft.interactionsSelected = values;
        })
      );
    },

    // Convenience method to update a single interaction flag
    setInteractionSelected: (key: keyof SelectedInteractionsPropsType, value: boolean) => {
      set(
        produce(draft => {
          draft.interactionsSelected[key] = value;
        })
      );
    },

    // Reset all interactions to false (useful for testing and cleanup)
    resetInteractions: () => {
      set(
        produce(draft => {
          Object.keys(draft.interactionsSelected).forEach(key => {
            draft.interactionsSelected[key as keyof SelectedInteractionsPropsType] = false;
          });
        })
      );
    },

    // Initialize store with existing interaction state during migration
    initializeInteractions: (interactions: Partial<SelectedInteractionsPropsType>) => {
      set(
        produce(draft => {
          Object.keys(interactions).forEach(key => {
            const typedKey = key as keyof SelectedInteractionsPropsType;
            if (interactions[typedKey] !== undefined) {
              draft.interactionsSelected[typedKey] = interactions[typedKey]!;
            }
          });
        })
      );
    },

    // Set revision token from validate response
    setRevisionToken: (token: string | null) => {
      set(
        produce(draft => {
          draft.revisionToken = token;
        })
      );
    },

    // Set entire session object
    setSessionObject: (sessionObject: SessionUpdateState['sessionObject']) => {
      set(
        produce(draft => {
          draft.sessionObject = sessionObject;
        })
      );
    },

    // Update specific fields of session object
    updateSessionObject: (updates: Partial<NonNullable<SessionUpdateState['sessionObject']>>) => {
      set(
        produce(draft => {
          if (!draft.sessionObject) {
            draft.sessionObject = {
              consumerID: '',
              accessToken: '',
              sessionID: '',
            };
          }
          Object.assign(draft.sessionObject, updates);
        })
      );
    },
  };
};

// Create the store using the existing createAppStore pattern
// usePersistentStore: false since session interactions are temporary UI state
export const useSessionUpdateZustandStore = createAppStore(sessionUpdateZustandStore, {
  usePersistentStore: false,
});

// Selector for easy access to revisionToken
export const useRevisionToken = () => useSessionUpdateZustandStore(state => state.revisionToken);

// Selector for easy access to sessionObject
export const useSessionObject = () => useSessionUpdateZustandStore(state => state.sessionObject);
